import React, { useEffect, useState, Suspense } from "react";
import {
    Input,
    InputGroup,
    InputRightElement,
    Button,
    IconButton,
    Text,
    FormControl,
    FormLabel,
    useToast,
    CircularProgress,
    Center,
    Box,
    Heading,
    Container,
    VStack,
    HStack,
    Icon,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    FiLock,
    FiCheckCircle,
    FiXCircle,
    FiArrowLeft,
} from "react-icons/fi";
import { supabase } from "./utils/supabaseClient";

function getPasswordStrength(password) {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 1) return { score, label: "Weak", color: "red.500" };
    if (score <= 2) return { score, label: "Fair", color: "orange.500" };
    if (score <= 3) return { score, label: "Good", color: "yellow.500" };
    if (score <= 4) return { score, label: "Strong", color: "green.500" };
    return { score, label: "Very strong", color: "emerald.500" };
}

function PasswordStrengthBar({ password }) {
    const strength = getPasswordStrength(password);
    return (
        <Box pt={1}>
            <HStack spacing={1}>
                {[1, 2, 3, 4, 5].map((i) => (
                    <Box
                        key={i}
                        flex={1}
                        h="6px"
                        borderRadius="full"
                        bg={i <= strength.score ? strength.color : "gray.200"}
                        transition="all 0.3s"
                    />
                ))}
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1}>
                Strength:{" "}
                <Text as="span" fontWeight="semibold" color={strength.color}>
                    {strength.label}
                </Text>
            </Text>
        </Box>
    );
}

function ResetPasswordInner() {
    const navigate = useNavigate();
    const toast = useToast();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState("verifying");
    const [errorMessage, setErrorMessage] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
    const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword;
    const canSubmit = password.length > 0 && passwordsMatch && !loading;

    useEffect(() => {
        const verifyLink = async () => {
            try {
                // Supabase often sends recovery links with tokens in the hash (#access_token=…&type=recovery),
                // not as ?code= (PKCE). parseSessionFromUrl is off in supabaseClient, so we apply the session here.
                const hash =
                    typeof window !== "undefined" && window.location.hash?.length > 1
                        ? window.location.hash.slice(1)
                        : "";
                const hashParams = hash ? new URLSearchParams(hash) : null;

                if (hashParams) {
                    const hashError = hashParams.get("error");
                    const hashErrorDesc = hashParams.get("error_description");
                    if (hashError) {
                        setStatus("error");
                        setErrorMessage(
                            hashErrorDesc || "The reset link is invalid or has expired."
                        );
                        window.history.replaceState(
                            null,
                            "",
                            window.location.pathname + window.location.search
                        );
                        return;
                    }

                    const access_token = hashParams.get("access_token");
                    const refresh_token = hashParams.get("refresh_token");
                    const type = hashParams.get("type");
                    if (
                        access_token &&
                        refresh_token &&
                        (type === "recovery" || type === null)
                    ) {
                        const { error: sessionError } = await supabase.auth.setSession({
                            access_token,
                            refresh_token,
                        });
                        window.history.replaceState(
                            null,
                            "",
                            window.location.pathname + window.location.search
                        );
                        if (sessionError) {
                            setStatus("error");
                            setErrorMessage(
                                "This reset link is invalid or has expired. Please request a new one."
                            );
                            return;
                        }
                        setStatus("ready");
                        return;
                    }
                    // Hash present but not recovery tokens — strip hash and continue checks below
                    if (access_token || hashParams.has("type")) {
                        window.history.replaceState(
                            null,
                            "",
                            window.location.pathname + window.location.search
                        );
                    }
                }

                const code = searchParams.get("code");
                const errParam = searchParams.get("error");
                const errorDescription = searchParams.get("error_description");

                if (errParam) {
                    setStatus("error");
                    setErrorMessage(
                        errorDescription || "The reset link is invalid or has expired."
                    );
                    return;
                }

                if (code) {
                    const { error: exchangeError } =
                        await supabase.auth.exchangeCodeForSession(code);
                    if (exchangeError) {
                        setStatus("error");
                        setErrorMessage(
                            "This reset link is invalid or has expired. Please request a new one."
                        );
                        return;
                    }
                    setStatus("ready");
                    return;
                }

                const {
                    data: { session },
                } = await supabase.auth.getSession();
                if (session) {
                    setStatus("ready");
                } else {
                    setStatus("error");
                    setErrorMessage("No valid reset link found. Please request a new one.");
                }
            } catch {
                setStatus("error");
                setErrorMessage("An unexpected error occurred. Please try again.");
            }
        };

        verifyLink();
    }, [searchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            toast({
                title: "Passwords do not match",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({ password });
            if (error) throw error;
            await supabase.auth.signOut();
            setStatus("success");
            setTimeout(() => navigate("/login"), 3000);
        } catch (err) {
            toast({
                title: err.message || "Failed to update password. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    if (status === "verifying") {
        return (
            <Box
                minH="100vh"
                w="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                px={4}
                bg={{ base: "gray.50", md: "white" }}
            >
                <Container maxW="container.sm">
                    <Box
                        bg="white"
                        p={8}
                        borderRadius="lg"
                        boxShadow="md"
                        textAlign="center"
                    >
                        <Center py={4}>
                            <CircularProgress isIndeterminate color="blue.500" />
                        </Center>
                        <Heading size="sm" mt={4}>
                            Verifying link…
                        </Heading>
                        <Text fontSize="sm" color="gray.600" mt={2}>
                            Please wait a moment.
                        </Text>
                    </Box>
                </Container>
            </Box>
        );
    }

    if (status === "error") {
        return (
            <Box
                minH="100vh"
                w="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                px={4}
                py={8}
                bg={{ base: "gray.50", md: "white" }}
            >
                <Container maxW="container.sm">
                    <Box
                        bg="white"
                        p={{ base: 6, md: 8 }}
                        borderRadius="lg"
                        boxShadow="md"
                        textAlign="center"
                    >
                        <Box
                            w={14}
                            h={14}
                            borderRadius="full"
                            bg="red.50"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            mx="auto"
                            mb={4}
                        >
                            <Icon as={FiXCircle} boxSize={7} color="red.600" />
                        </Box>
                        <Heading size="sm" color="red.700">
                            Link expired or invalid
                        </Heading>
                        <Text fontSize="sm" color="gray.600" mt={3}>
                            {errorMessage}
                        </Text>
                        <VStack mt={6} spacing={3}>
                            <Button
                                as={Link}
                                to="/forgot-password"
                                width="full"
                                color="white"
                                bg="gray.800"
                                _hover={{ bg: "gray.700" }}
                            >
                                Request a new reset link
                            </Button>
                            <Button as={Link} to="/login" variant="ghost" width="full">
                                Back to login
                            </Button>
                        </VStack>
                    </Box>
                </Container>
            </Box>
        );
    }

    if (status === "success") {
        return (
            <Box
                minH="100vh"
                w="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                px={4}
                bg={{ base: "gray.50", md: "white" }}
            >
                <Container maxW="container.sm">
                    <Box
                        bg="white"
                        p={8}
                        borderRadius="lg"
                        boxShadow="md"
                        textAlign="center"
                    >
                        <Box
                            w={14}
                            h={14}
                            borderRadius="full"
                            bg="green.50"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            mx="auto"
                            mb={4}
                        >
                            <Icon as={FiCheckCircle} boxSize={7} color="green.600" />
                        </Box>
                        <Heading size="sm" color="green.700">
                            Password updated!
                        </Heading>
                        <Text fontSize="sm" color="gray.600" mt={3}>
                            Your password has been reset successfully. Redirecting you to login…
                        </Text>
                        <Center mt={4}>
                            <CircularProgress isIndeterminate size="sm" color="blue.500" />
                        </Center>
                    </Box>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            minH="100vh"
            w="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={{ base: 4, md: 0 }}
            py={8}
            bg={{ base: "gray.50", md: "white" }}
        >
            <Container maxW="container.sm" w="100%" px={{ base: 0, md: 4 }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <Box
                        bg="white"
                        p={{ base: 6, md: 8 }}
                        borderRadius="lg"
                        boxShadow={{ base: "sm", md: "md" }}
                        textAlign="center"
                        w="100%"
                    >
                        <VStack spacing={5} align="stretch">
                            <Box textAlign="center">
                                <Box
                                    w={14}
                                    h={14}
                                    borderRadius="full"
                                    bg="blue.50"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mx="auto"
                                    mb={3}
                                >
                                    <Icon as={FiLock} boxSize={7} color="blue.600" />
                                </Box>
                                <Heading size="sm">Set a new password</Heading>
                                <Text fontSize="sm" color="gray.600" mt={2}>
                                    Choose a new password for your account.
                                </Text>
                            </Box>

                            <Box as="form" onSubmit={handleSubmit}>
                                <FormControl>
                                    <FormLabel fontSize={{ base: "sm", md: "md" }}>
                                        New password
                                    </FormLabel>
                                    <InputGroup size={{ base: "md", md: "lg" }}>
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter new password"
                                            bg="white"
                                            pr="12"
                                            fontSize={{ base: "16px", md: "md" }}
                                            minH={{ base: "48px", md: "40px" }}
                                            autoComplete="new-password"
                                        />
                                        <InputRightElement
                                            height={{ base: "48px", md: "40px" }}
                                            width="12"
                                        >
                                            <IconButton
                                                variant="ghost"
                                                size="sm"
                                                aria-label={
                                                    showPassword ? "Hide password" : "Show password"
                                                }
                                                icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                                                onClick={() => setShowPassword((v) => !v)}
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                    {password ? <PasswordStrengthBar password={password} /> : null}
                                </FormControl>

                                <FormControl mt={5}>
                                    <FormLabel fontSize={{ base: "sm", md: "md" }}>
                                        Confirm new password
                                    </FormLabel>
                                    <InputGroup size={{ base: "md", md: "lg" }}>
                                        <Input
                                            type={showConfirmPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Repeat your new password"
                                            bg="white"
                                            pr="12"
                                            fontSize={{ base: "16px", md: "md" }}
                                            minH={{ base: "48px", md: "40px" }}
                                            autoComplete="new-password"
                                        />
                                        <InputRightElement
                                            height={{ base: "48px", md: "40px" }}
                                            width="12"
                                        >
                                            <IconButton
                                                variant="ghost"
                                                size="sm"
                                                aria-label={
                                                    showConfirmPassword
                                                        ? "Hide password"
                                                        : "Show password"
                                                }
                                                icon={
                                                    showConfirmPassword ? (
                                                        <ViewOffIcon />
                                                    ) : (
                                                        <ViewIcon />
                                                    )
                                                }
                                                onClick={() => setShowConfirmPassword((v) => !v)}
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                    {passwordMismatch && (
                                        <Text fontSize="xs" color="red.500" mt={1} textAlign="left">
                                            Passwords do not match
                                        </Text>
                                    )}
                                    {passwordsMatch && (
                                        <Text fontSize="xs" color="green.600" mt={1} textAlign="left">
                                            Passwords match
                                        </Text>
                                    )}
                                </FormControl>

                                <Button
                                    type="submit"
                                    mt={6}
                                    width="full"
                                    height={{ base: "48px", md: "44px" }}
                                    color="white"
                                    bg="gray.800"
                                    _hover={{ bg: "gray.700" }}
                                    isDisabled={!canSubmit}
                                    isLoading={loading}
                                    fontWeight="semibold"
                                >
                                    Update password
                                </Button>
                            </Box>
                        </VStack>
                    </Box>
                </motion.div>
            </Container>

            <Box mt={6}>
                <Button
                    as={Link}
                    to="/login"
                    variant="ghost"
                    size="sm"
                    leftIcon={<FiArrowLeft />}
                    color="gray.600"
                >
                    Back to login
                </Button>
            </Box>

            <HStack mt={8} spacing={1.5} fontSize="xs" color="gray.400">
                <Text>Powered by</Text>
                <Box
                    as="img"
                    src={`${process.env.PUBLIC_URL || ""}/Edthos.png`}
                    alt="Edthos"
                    h="16px"
                    w="auto"
                    objectFit="contain"
                    opacity={0.4}
                />
            </HStack>
        </Box>
    );
}

function ResetPasswordLoading() {
    return (
        <Box
            minH="100vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="gray.50"
        >
            <CircularProgress isIndeterminate />
        </Box>
    );
}

const ResetPasswordPage = () => (
    <Suspense fallback={<ResetPasswordLoading />}>
        <ResetPasswordInner />
    </Suspense>
);

export default ResetPasswordPage;
